(function () {
  const MIN_CHECKOUT_CENTS = 39900;
  const CHECKOUT_SELECTOR = '[data-at-checkout-button], button[name="checkout"]';
  const CART_FORM_SELECTOR = '#cart, #CartDrawer-Form';
  const DYNAMIC_CHECKOUT_SELECTOR = '[data-at-dynamic-checkout], .cart__dynamic-checkout-buttons';
  const UPSELL_FORM_SELECTOR = '.at-cart-upsell__form form';
  const UPSELL_VARIANT_SELECTOR = '[data-at-upsell-variant-select]';
  const CART_VARIANT_OPTION_SELECTOR = '[data-at-cart-variant-option]';
  const UPSELL_SECTION_SELECTOR = '[data-at-cart-upsell]';
  const UPSELL_HEADING_SELECTOR = '.at-cart-upsell__heading';
  const UPSELL_HIGHLIGHT_CLASS = 'at-cart-upsell--highlight';
  const UPSELL_HIGHLIGHT_MS = 1800;
  const LINE_EDIT_SELECTOR = '[data-at-cart-line-edit]';

  let latestCart = readCartFromDom();
  let refreshTimer = null;
  let observer = null;
  let cartVariantUpdateInFlight = false;
  let upsellHighlightTimer = null;
  let upsellHighlighted = null;
  const observedRegions = new WeakSet();

  function parseMoneyValue(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function readCartFromDom() {
    const source =
      document.querySelector('cart-drawer[data-at-cart-total]') ||
      document.querySelector('[data-at-cart-total]');

    return {
      total_price: parseMoneyValue(source?.dataset.atCartTotal) || 0,
      items_subtotal_price:
        parseMoneyValue(source?.dataset.atCartSubtotal) || parseMoneyValue(source?.dataset.atCartTotal) || 0,
      item_count: parseMoneyValue(source?.dataset.atCartCount) || 0,
    };
  }

  function hasUsableCart(cart) {
    return (
      cart &&
      Number.isFinite(Number(cart.total_price)) &&
      Number.isFinite(Number(cart.item_count)) &&
      Number.isFinite(Number(cart.items_subtotal_price ?? cart.total_price))
    );
  }

  function isBlocked(cart = latestCart) {
    const checkoutValue = Number(cart?.items_subtotal_price ?? cart?.total_price);
    return !hasUsableCart(cart) || Number(cart.item_count) < 1 || checkoutValue < MIN_CHECKOUT_CENTS;
  }

  function formatRemainingAmount(cents) {
    const safeCents = Math.max(0, Number(cents) || 0);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(safeCents);
    }

    const rupees = Math.ceil(safeCents / 100);
    return `₹${rupees.toLocaleString('en-IN')}`;
  }

  function setCheckoutButtonLabel(button, blocked, remainingCents) {
    const label = button.querySelector('[data-at-checkout-label]') || button;
    const readyLabel = button.dataset.atCheckoutReadyLabel || label.textContent.trim();
    const blockedTemplate = button.dataset.atCheckoutBlockedTemplate || 'Add [amount] more to checkout';

    label.textContent = blocked ? blockedTemplate.replace('[amount]', formatRemainingAmount(remainingCents)) : readyLabel;
  }

  function applyGate(cart = latestCart) {
    if (!hasUsableCart(cart)) return;

    latestCart = {
      total_price: Number(cart.total_price),
      items_subtotal_price: Number(cart.items_subtotal_price ?? cart.total_price),
      item_count: Number(cart.item_count),
    };

    const blocked = isBlocked(latestCart);
    const checkoutValue = Number(latestCart.items_subtotal_price ?? latestCart.total_price);
    const remainingCents = MIN_CHECKOUT_CENTS - checkoutValue;

    document.querySelectorAll('[data-at-cart-total]').forEach((element) => {
      element.dataset.atCartTotal = String(latestCart.total_price);
      element.dataset.atCartSubtotal = String(latestCart.items_subtotal_price);
      element.dataset.atCartCount = String(latestCart.item_count);
    });

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (!button.matches('[data-at-checkout-button], #checkout, #CartDrawer-Checkout')) return;

      // Blocked buttons stay tappable (aria-disabled, never the native disabled attribute) so a tap can
      // guide the shopper to the suggestions. While blocked the button is type="button" so it cannot submit
      // its form; handleClick/handleSubmit below still cancel every checkout path as a second guard.
      button.removeAttribute('disabled');
      button.setAttribute('type', blocked ? 'button' : 'submit');
      button.classList.toggle('at-cart-gated', blocked);
      button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      button.dataset.atCheckoutBlocked = blocked ? 'true' : 'false';
      if (button.dataset.atCheckoutHintId) {
        if (blocked) {
          button.setAttribute('aria-describedby', button.dataset.atCheckoutHintId);
        } else {
          button.removeAttribute('aria-describedby');
        }
      }
      setCheckoutButtonLabel(button, blocked, remainingCents);
    });

    document.querySelectorAll(CART_FORM_SELECTOR).forEach((form) => {
      form.dataset.atCheckoutBlocked = blocked ? 'true' : 'false';
    });

    document.querySelectorAll(DYNAMIC_CHECKOUT_SELECTOR).forEach((container) => {
      container.hidden = blocked;
      container.setAttribute('aria-hidden', blocked ? 'true' : 'false');
    });

    syncVariantControls();
  }

  function refreshCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => applyGate(cart))
      .catch(() => applyGate(readCartFromDom()));
  }

  function getCartJson() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then((response) => response.json());
  }

  function refreshSoon() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshCart, 80);
  }

  function getSubmitButton(form, submitter) {
    if (submitter?.matches?.('button, [type="submit"]')) return submitter;
    return form.querySelector('[type="submit"]');
  }

  function setUpsellLoading(button, isLoading) {
    if (!button) return;

    button.disabled = isLoading;
    button.classList.toggle('loading', isLoading);
    button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
  }

  function getCartSectionIds() {
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionIds = new Set();

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      cartDrawer.getSectionsToRender().forEach((section) => sectionIds.add(section.id));
    }

    if (document.getElementById('main-cart-items')) sectionIds.add('main-cart-items');
    if (document.getElementById('main-cart-footer')) sectionIds.add('main-cart-footer');
    sectionIds.add('cart-icon-bubble');

    return Array.from(sectionIds);
  }

  function getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  function renderSection(sectionId, html, selector = '.shopify-section') {
    const target = document.getElementById(sectionId);
    if (!target || !html) return;

    const source = getSectionInnerHTML(html, selector);
    const targetElement = target.querySelector(selector) || target;
    if (source) targetElement.innerHTML = source;
  }

  function renderUpsellCartState(response) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && response?.sections && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(response);
    }

    if (!response?.sections) return false;

    renderSection('main-cart-items', response.sections['main-cart-items'], '.js-contents');
    renderSection('main-cart-footer', response.sections['main-cart-footer'], '.js-contents');
    renderSection('cart-icon-bubble', response.sections['cart-icon-bubble']);

    return true;
  }

  function refreshRenderedCartSections() {
    const cartItems = Array.from(document.querySelectorAll('cart-drawer-items, cart-items'));
    const refreshes = cartItems.map((cartItemsElement) => {
      if (typeof cartItemsElement.onCartUpdate !== 'function') return Promise.resolve();

      return cartItemsElement.onCartUpdate();
    });

    return Promise.all(refreshes);
  }

  function publishCartState(cart, variantId) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'arttheory-cart', cartData: cart, variantId });
    }

    document.dispatchEvent(new CustomEvent('cart:change', { detail: cart }));
  }

  function submitUpsellForm(form, submitter) {
    if (form.dataset.atUpsellSubmitting === 'true') return;

    const button = getSubmitButton(form, submitter);
    const formData = new FormData(form);
    const sectionIds = getCartSectionIds();
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);
    form.dataset.atUpsellSubmitting = 'true';
    setUpsellLoading(button, true);

    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : {
            method: 'POST',
            headers: { Accept: 'application/json' },
          };

    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(window.routes?.cart_add_url || '/cart/add', config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Product add failed');
        }

        renderUpsellCartState(response);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: response }));
        return refreshCart();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        form.dataset.atUpsellSubmitting = 'false';
        setUpsellLoading(button, false);
      });
  }

  function postJson(url, body) {
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
    config.body = JSON.stringify(body);
    return fetch(url, config).then((response) => response.json());
  }

  function parseJsonElement(element, fallback) {
    if (!element?.textContent) return fallback;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error(error);
      return fallback;
    }
  }

  function normalizeProperties(properties) {
    if (!properties) return {};
    if (!Array.isArray(properties)) return properties;

    return properties.reduce((normalized, property) => {
      if (Array.isArray(property) && property.length >= 2) {
        normalized[property[0]] = property[1];
      }

      return normalized;
    }, {});
  }

  function hasProperties(properties) {
    return Object.values(normalizeProperties(properties)).some((value) => value !== null && String(value) !== '');
  }

  function hasSellingPlan(cartItem) {
    return Boolean(cartItem?.selling_plan_allocation || cartItem?.selling_plan);
  }

  function findCartItemByKey(cart, key) {
    return cart?.items?.find((item) => item.key === key);
  }

  function getVariantId(cartItem) {
    return String(cartItem?.variant_id || cartItem?.id || '');
  }

  function getPlainVariantQuantity(cart, variantId, excludedKey) {
    return (cart?.items || []).reduce((quantity, item) => {
      if (item.key === excludedKey) return quantity;
      if (getVariantId(item) !== String(variantId)) return quantity;
      if (hasProperties(item.properties) || hasSellingPlan(item)) return quantity;

      return quantity + (Number.parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  function hasSpecialTargetLine(cart, variantId, excludedKey) {
    return (cart?.items || []).some((item) => {
      if (item.key === excludedKey) return false;
      if (getVariantId(item) !== String(variantId)) return false;

      return hasProperties(item.properties) || hasSellingPlan(item);
    });
  }

  function setGlobalCartLineVariantLoading(isLoading) {
    cartVariantUpdateInFlight = isLoading;

    document.querySelectorAll('[data-at-cart-line]').forEach((lineItem) => {
      setCartLineVariantLoading(lineItem, isLoading);
    });
  }

  function setCartLineVariantLoading(lineItem, isLoading) {
    lineItem.dataset.atVariantUpdating = isLoading ? 'true' : 'false';
    lineItem.classList.toggle('at-cart-line--updating', isLoading);

    lineItem.querySelectorAll(CART_VARIANT_OPTION_SELECTOR).forEach((select) => {
      select.disabled = isLoading;
    });
  }

  function getSelectedCartLineVariant(lineItem) {
    const variants = parseJsonElement(lineItem.querySelector('[data-at-cart-line-variants]'), []);
    const selectedOptions = Array.from(lineItem.querySelectorAll(CART_VARIANT_OPTION_SELECTOR)).map(
      (select) => select.value
    );

    return variants.find((variant) => {
      if (!variant.available) return false;
      return selectedOptions.every((option, index) => variant.options[index] === option);
    });
  }

  function updateCartLineOptionAvailability(lineItem) {
    const variants = parseJsonElement(lineItem.querySelector('[data-at-cart-line-variants]'), []);
    const selects = Array.from(lineItem.querySelectorAll(CART_VARIANT_OPTION_SELECTOR));
    const selectedOptions = selects.map((select) => select.value);

    selects.forEach((select, optionIndex) => {
      Array.from(select.options).forEach((option) => {
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = option.value;
        const hasAvailableVariant = variants.some((variant) => {
          if (!variant.available) return false;
          return candidateOptions.every((candidate, index) => variant.options[index] === candidate);
        });

        option.disabled = !hasAvailableVariant && option.value !== select.value;
      });
    });
  }

  function syncVariantControls() {
    document.querySelectorAll('[data-at-cart-line]').forEach((lineItem) => {
      updateCartLineOptionAvailability(lineItem);
    });
  }

  function setCartLineSelectsToVariant(lineItem, variantId) {
    const variants = parseJsonElement(lineItem.querySelector('[data-at-cart-line-variants]'), []);
    const variant = variants.find((entry) => String(entry.id) === String(variantId));
    if (!variant) return;

    lineItem.querySelectorAll(CART_VARIANT_OPTION_SELECTOR).forEach((select, index) => {
      if (variant.options[index]) select.value = variant.options[index];
    });
  }

  function changeCartLineVariant(select) {
    const lineItem = select.closest('[data-at-cart-line]');
    if (!lineItem || cartVariantUpdateInFlight || lineItem.dataset.atVariantUpdating === 'true') return;

    updateCartLineOptionAvailability(lineItem);
    const variant = getSelectedCartLineVariant(lineItem);
    if (!variant) {
      setCartLineSelectsToVariant(lineItem, lineItem.dataset.currentVariantId);
      updateCartLineOptionAvailability(lineItem);
      return;
    }
    if (String(variant.id) === lineItem.dataset.currentVariantId) return;

    const sectionIds = getCartSectionIds();
    const sectionPayload = sectionIds.join(',');
    const lineKey = lineItem.dataset.key;
    const selectedVariantId = Number(variant.id);

    setGlobalCartLineVariantLoading(true);

    getCartJson()
      .then((cart) => {
        const currentItem = findCartItemByKey(cart, lineKey);

        if (!currentItem) {
          throw new Error('Cart line is stale');
        }

        const quantity = Number.parseInt(currentItem.quantity, 10) || Number.parseInt(lineItem.dataset.quantity, 10) || 1;
        const properties = normalizeProperties(currentItem.properties);
        const currentHasSpecialData = hasProperties(properties) || hasSellingPlan(currentItem);
        const targetHasSpecialLine = hasSpecialTargetLine(cart, selectedVariantId, currentItem.key);

        if (!currentHasSpecialData && !targetHasSpecialLine) {
          const updates = {};
          updates[currentItem.key] = 0;
          updates[selectedVariantId] = getPlainVariantQuantity(cart, selectedVariantId, currentItem.key) + quantity;

          return postJson(window.routes?.cart_update_url || '/cart/update.js', {
            updates,
            sections: sectionPayload,
            sections_url: window.location.pathname,
          });
        }

        const addBody = {
          id: selectedVariantId,
          quantity,
          sections: sectionPayload,
          sections_url: window.location.pathname,
        };

        if (Object.keys(properties).length > 0) addBody.properties = properties;
        if (currentItem.selling_plan_allocation?.selling_plan?.id) {
          addBody.selling_plan = currentItem.selling_plan_allocation.selling_plan.id;
        } else if (lineItem.dataset.sellingPlanId) {
          addBody.selling_plan = lineItem.dataset.sellingPlanId;
        }

        return postJson(window.routes?.cart_change_url || '/cart/change.js', {
          id: currentItem.key,
          quantity: 0,
          sections: sectionPayload,
          sections_url: window.location.pathname,
        }).then((response) => {
          if (response.status || response.errors) {
            throw new Error(response.description || response.message || response.errors || 'Cart variant removal failed');
          }

          return postJson(window.routes?.cart_add_url || '/cart/add.js', addBody);
        });
      })
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Cart variant update failed');
        }

        if (response.errors) {
          throw new Error(response.errors);
        }

        const renderPromise = renderUpsellCartState(response)
          ? Promise.resolve()
          : refreshRenderedCartSections();

        return renderPromise.then(() => {
          publishCartState(response, selectedVariantId);
          return refreshCart();
        });
      })
      .catch((error) => {
        console.error(error);
        setCartLineSelectsToVariant(lineItem, lineItem.dataset.currentVariantId);
        updateCartLineOptionAvailability(lineItem);
        refreshSoon();
      })
      .finally(() => {
        setGlobalCartLineVariantLoading(false);
      });
  }

  function updateUpsellVariantPrice(select) {
    const price = select.selectedOptions?.[0]?.dataset.price;
    const priceElement = select.closest('.at-cart-upsell__item')?.querySelector('[data-at-upsell-price]');
    if (price && priceElement) priceElement.textContent = price;
  }

  function handleChange(event) {
    const cartVariantSelect = event.target.closest?.(CART_VARIANT_OPTION_SELECTOR);
    if (cartVariantSelect) {
      event.stopImmediatePropagation();
      changeCartLineVariant(cartVariantSelect);
      return;
    }

    const upsellVariantSelect = event.target.closest?.(UPSELL_VARIANT_SELECTOR);
    if (upsellVariantSelect) {
      event.stopImmediatePropagation();
      updateUpsellVariantPrice(upsellVariantSelect);
    }
  }

  function isCheckoutTrigger(target) {
    const button = target?.closest?.(CHECKOUT_SELECTOR);
    if (!button) return false;

    return button.matches('[data-at-checkout-button], #checkout, #CartDrawer-Checkout');
  }

  function preventWhenBlocked(event) {
    if (!isBlocked()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    refreshSoon();
  }

  function findSuggestionsSection(button) {
    const scope = button.closest('cart-drawer') || document.querySelector('.at-cart-page-upsell');
    const section = scope?.querySelector(UPSELL_SECTION_SELECTOR);

    // Nothing rendered (no recommendations) or not visible (empty cart): there is nothing to show.
    return section && section.getClientRects().length > 0 ? section : null;
  }

  function highlightSuggestions(section) {
    window.clearTimeout(upsellHighlightTimer);
    upsellHighlighted?.classList.remove(UPSELL_HIGHLIGHT_CLASS);

    section.classList.remove(UPSELL_HIGHLIGHT_CLASS);
    void section.offsetWidth; // restart the CSS animation on repeated taps
    section.classList.add(UPSELL_HIGHLIGHT_CLASS);
    upsellHighlighted = section;

    upsellHighlightTimer = window.setTimeout(() => {
      section.classList.remove(UPSELL_HIGHLIGHT_CLASS);
      if (upsellHighlighted === section) upsellHighlighted = null;
    }, UPSELL_HIGHLIGHT_MS);
  }

  // Tapping the gated checkout button never checks out; it brings the "Complete your wall" suggestions into view.
  function guideToSuggestions(button) {
    const section = findSuggestionsSection(button);
    if (!section) return false;

    const reduceMotion = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });

    const heading = section.querySelector(UPSELL_HEADING_SELECTOR);
    if (heading) heading.focus({ preventScroll: true });

    highlightSuggestions(section);
    return true;
  }

  // Drawer lines keep their size/finish selects behind an "Edit" button (progressive disclosure). The selects stay in
  // the DOM, so changeCartLineVariant above still handles every change; the cart re-render closes the panel again.
  function toggleLineOptions(event) {
    const button = event.target.closest?.(LINE_EDIT_SELECTOR);
    if (!button) return;

    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;

    event.preventDefault();
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;

    const label = button.querySelector('[data-at-cart-line-edit-label]');
    const text = open ? button.dataset.labelOpen : button.dataset.labelClosed;
    if (label && text) label.textContent = text;

    if (open) {
      panel.scrollIntoView({ block: 'nearest' });
      panel.querySelector('select')?.focus({ preventScroll: true });
    }
  }

  function isImplicitSubmission(event) {
    // Enter inside a quantity/note field makes the browser click the form's default button with detail 0 while
    // the field keeps focus; that is not a tap on the button (a real tap has detail >= 1, Enter on the button
    // itself leaves the button focused).
    return event.detail === 0 && Boolean(document.activeElement?.matches?.('input, textarea, select'));
  }

  function handleClick(event) {
    if (!isCheckoutTrigger(event.target)) return;

    const blocked = isBlocked();
    preventWhenBlocked(event);

    if (blocked && !isImplicitSubmission(event)) {
      guideToSuggestions(event.target.closest(CHECKOUT_SELECTOR));
    }
  }

  function handleSubmit(event) {
    const form = event.target;
    if (form.matches(UPSELL_FORM_SELECTOR)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      submitUpsellForm(form, event.submitter || document.activeElement);
      return;
    }

    if (!form.matches(CART_FORM_SELECTOR)) return;

    const submitter = event.submitter || document.activeElement;
    const checkoutSubmit = submitter?.matches?.('[data-at-checkout-button], button[name="checkout"]');
    if (checkoutSubmit || form.dataset.atCheckoutBlocked === 'true') preventWhenBlocked(event);
  }

  function observeCartRegions() {
    if (!observer) return;

    [
      document.body,
      document.getElementById('CartDrawer'),
      document.querySelector('.drawer__inner'),
      document.querySelector('cart-items'),
      document.getElementById('main-cart-footer'),
    ]
      .filter(Boolean)
      .forEach((region) => {
        if (observedRegions.has(region)) return;
        observedRegions.add(region);
        observer.observe(region, { childList: true });
      });
  }

  function bindPubSub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (hasUsableCart(event?.cartData)) {
        applyGate(event.cartData);
        return;
      }

      refreshSoon();
    });
  }

  function init() {
    applyGate(readCartFromDom());
    refreshCart();

    document.addEventListener('click', handleClick, true);
    document.addEventListener('click', toggleLineOptions);
    document.addEventListener('change', handleChange, true);
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('cart:change', refreshSoon);
    document.addEventListener('cart:refresh', refreshSoon);
    document.addEventListener('shopify:section:load', refreshSoon);

    observer = new MutationObserver(() => {
      applyGate(readCartFromDom());
      observeCartRegions();
      refreshSoon();
    });
    observeCartRegions();
    bindPubSub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.ArtTheoryCart = {
    MIN_CHECKOUT_CENTS,
    syncGate: refreshCart,
  };
})();
